// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skein — the geometry the whole game rests on: do two threads cross? A "crossing" is a
// PROPER intersection of two straight segments that do NOT share an endpoint (two threads
// meeting at a shared bead are adjacent, never crossed). Everything downstream — the win
// condition, the scoring, the renderer's colouring — is a function of countCrossings, so
// it is deliberately tiny, pure, integer-orientation-based, and unit-tested to death.

export interface Pt {
  x: number;
  y: number;
}

export type Edge = readonly [number, number];

/** Sign of the signed area of triangle (a,b,c): +1 ccw, -1 cw, 0 collinear. */
export function orient(a: Pt, b: Pt, c: Pt): number {
  const v = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  return v > 1e-12 ? 1 : v < -1e-12 ? -1 : 0;
}

/**
 * True iff segment ab PROPERLY crosses segment cd — they intersect at a single interior
 * point of both. Touching at an endpoint or overlapping collinearly does NOT count; those
 * are handled by excluding shared-vertex edge pairs and by the graph never being collinear.
 */
export function segmentsCross(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);
  return o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0 && o1 !== o2 && o3 !== o4;
}

/** Two edges are adjacent if they share a bead — adjacency is never a crossing. */
function shareVertex(e: Edge, f: Edge): boolean {
  return e[0] === f[0] || e[0] === f[1] || e[1] === f[0] || e[1] === f[1];
}

/** Total number of crossing thread-pairs in this layout. Zero means solved. */
export function countCrossings(pts: readonly Pt[], edges: readonly Edge[]): number {
  let n = 0;
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i];
    const a = pts[e[0]];
    const b = pts[e[1]];
    for (let j = i + 1; j < edges.length; j++) {
      const f = edges[j];
      if (shareVertex(e, f)) continue;
      if (segmentsCross(a, b, pts[f[0]], pts[f[1]])) n++;
    }
  }
  return n;
}

/** Per-edge flag: is this thread involved in at least one crossing? (renderer colouring) */
export function crossedEdges(pts: readonly Pt[], edges: readonly Edge[]): boolean[] {
  const flags = edges.map(() => false);
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i];
    const a = pts[e[0]];
    const b = pts[e[1]];
    for (let j = i + 1; j < edges.length; j++) {
      const f = edges[j];
      if (shareVertex(e, f)) continue;
      if (segmentsCross(a, b, pts[f[0]], pts[f[1]])) {
        flags[i] = true;
        flags[j] = true;
      }
    }
  }
  return flags;
}

/** The intersection point of two crossing segments (only valid when they cross). */
export function intersection(a: Pt, b: Pt, c: Pt, d: Pt): Pt {
  const r1x = b.x - a.x;
  const r1y = b.y - a.y;
  const r2x = d.x - c.x;
  const r2y = d.y - c.y;
  const denom = r1x * r2y - r1y * r2x;
  if (Math.abs(denom) < 1e-12) return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const t = ((c.x - a.x) * r2y - (c.y - a.y) * r2x) / denom;
  return { x: a.x + t * r1x, y: a.y + t * r1y };
}

/** Every crossing point, for the ✕ markers. */
export function crossingMarks(pts: readonly Pt[], edges: readonly Edge[]): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i];
    const a = pts[e[0]];
    const b = pts[e[1]];
    for (let j = i + 1; j < edges.length; j++) {
      const f = edges[j];
      if (shareVertex(e, f)) continue;
      if (segmentsCross(a, b, pts[f[0]], pts[f[1]])) out.push(intersection(a, b, pts[f[0]], pts[f[1]]));
    }
  }
  return out;
}

export function dist(a: Pt, b: Pt): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Distance from point p to segment ab — used to keep threads from running through beads. */
export function pointSegDist(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return dist(p, a);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}
